<template>
  <b-form @submit.prevent>
    <b-card no-body class="radius-top-0 border-0">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <b class="d-block" v-text="pedidoId" />
          </div>

          <div class="d-flex align-items-center">
            <router-link
              :to="$routeBack('pedidos_list')"
              class="btn btn-white text-exitos"
            >
              Voltar para Listagem
            </router-link>
            <b-button
              v-if="1 > 1"
              :href="pagamento.boleto_url"
              target="_blank"
              rel="noopener noreferrer"
              variant="exitos"
              class="ml-2"
            >
              Baixar boleto atualizado
              <font-awesome-icon
                icon="up-right-from-square"
                class="ml-1 small"
              />
            </b-button>
          </div>
        </div>
      </template>

      <div class="py-4">
        <b-card class="mb-4">
          <template #header>
            <div class="font-weight-600">Resumo do Pedido</div>
          </template>

          <b-row>
            <b-col cols="12" md="6" class="mb-4">
              <div class="small text-gray">Cliente</div>
              <router-link
                v-if="!!pedido?.user?.id"
                v-b-tooltip.hover
                title="Detalhes do cliente"
                class="h3 font-weight-normal text-exitos"
                target="_blank"
                :to="{
                  name: 'users_edit',
                  params: { id: pedido.user?.id },
                }"
              >
                {{ pedido.user?.name || "-" }}
                <font-awesome-icon
                  icon="up-right-from-square"
                  class="text-exitos ml-1 small"
                />
              </router-link>
            </b-col>

            <b-col cols="12" md="6" class="mb-4">
              <div class="small text-gray">Valor total</div>
              <div
                class="h3 font-weight-normal"
                v-text="formatCurrency(pedido.valor_pedido)"
              />
            </b-col>

            <b-col cols="12" md="6" class="mb-4">
              <div class="small text-gray">Plano</div>
              <div class="h3 font-weight-normal">
                {{ pedido.plano?.nome || "-" }}
              </div>
            </b-col>

            <b-col cols="12" md="6" class="mb-4">
              <div class="small text-gray">Data</div>
              <div
                class="h3 font-weight-normal"
                v-text="formatDateTime(pedido.created_at)"
              />
            </b-col>

            <b-col cols="12" md="6" class="mb-4">
              <div class="small text-gray">Periodicidade</div>
              <div class="h3 font-weight-normal">
                {{ pedido.periodicidade?.periodicidade || "-" }}
              </div>
            </b-col>

            <b-col cols="12" md="6" class="mb-4">
              <div class="small text-gray">Método de pagamento</div>
              <div class="h3 font-weight-normal">
                {{ pedido.forma_pagamento || "-" }}
              </div>
            </b-col>
          </b-row>

          <div v-if="isCanceled" class="border-top pt-3">
            <h3 class="h4">Cancelamento do pedido</h3>
            <b-row>
              <b-col cols="12" md="6" class="mb-3">
                <div class="small text-gray">Responsável pelo cancelamento</div>
                <span v-text="cancellationAuthor" />
              </b-col>
              <b-col cols="12" md="6" class="mb-3">
                <div class="small text-gray">Data e hora do cancelamento</div>
                <div v-text="formatDateTime(pedido.cancelado_em)" />
              </b-col>
              <b-col cols="12" class="mb-3">
                <div class="small text-gray">Motivo do cancelamento</div>
                <div class="cancellation-reason">
                  {{ pedido.motivo || "Não informado" }}
                </div>
              </b-col>
            </b-row>
          </div>

          <b-card-footer v-if="allowedCancelOrder" class="pb-0 pt-2 px-0">
            <b-button variant="danger" size="sm" @click="onModalOrderDisplay">
              Cancelar pedido
            </b-button>
            <b-modal
              v-model="isModalOrderDisplay"
              centered
              title="Cancelar pedido?"
              header-class="align-items-center"
              title-class="h4"
              ok-title="Cancelar pedido"
              ok-variant="danger"
              cancel-variant="outline-exitos"
              cancel-title="Manter pedido"
              body-class="py-0 text-gray-dark"
              :ok-disabled="v$.$invalid || isCanceling"
              :cancel-disabled="isCanceling"
              :no-close-on-backdrop="isCanceling"
              :no-close-on-esc="isCanceling"
              :hide-header-close="isCanceling"
              @ok="cancelOrder"
            >
              <p>
                Ao cancelar este pedido, ele deixará de seguir para pagamento ou
                processamento. Esta ação não poderá ser desfeita.
              </p>
              <b-form-group
                label="Motivo do cancelamento"
                label-for="order-cancellation-reason"
                description="Explique o motivo do cancelamento com pelo menos 10 caracteres, desconsiderando os espaços no início e no fim."
              >
                <b-form-textarea
                  id="order-cancellation-reason"
                  v-model="motivo"
                  rows="3"
                  :disabled="isCanceling"
                  :state="reasonState"
                  aria-required="true"
                  @blur="v$.motivo.$touch()"
                />
                <b-form-invalid-feedback :state="reasonState">
                  {{ reasonError }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-modal>
          </b-card-footer>
        </b-card>

        <table-history :items="pagamentosHistorico" />
      </div>
    </b-card>
  </b-form>
</template>

<script>
import { computed, defineComponent, ref } from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers, minLength, required } from "@vuelidate/validators";
import {
  useNamespacedActions,
  useNamespacedState,
} from "vuex-composition-helpers";
import numberFormat from "@/helpers/filters/number_format";
import formatDate from "@/helpers/filters/format_date";
import { useRoute } from "@/composables/useRoute";
import { useNotify } from "@/composables/useNotify";
import TableHistory from "@/modules/pedidos/Components/TableHistory.vue";
import {
  enumMap,
  isStatus,
} from "@/modules/pedidos/Components/getParsedStatus";
import { extractErrorMessage } from "@/helpers/functions/errorHelpers";
import { CANCELLATION_ORIGINS } from "@/modules/pedidos/constants/cancellationOrigins";

export default defineComponent({
  components: { TableHistory },
  setup() {
    const route = useRoute();
    const notifications = useNotify();
    const { pedido, pagamento, pagamentosHistorico } = useNamespacedState(
      "pedidos",
      ["pedido", "pagamento", "pagamentosHistorico"]
    );

    const { ActionCancelOrder, ActionShowPaymentsHistory, ActionShow } =
      useNamespacedActions("pedidos", [
        "ActionCancelOrder",
        "ActionShowPaymentsHistory",
        "ActionShow",
      ]);

    const isModalOrderDisplay = ref(false);
    const isCanceling = ref(false);
    const motivo = ref("");
    const trimmedReason = computed(() => motivo.value.trim());

    const v$ = useVuelidate(
      {
        motivo: {
          required: helpers.withMessage(
            "Informe o motivo do cancelamento.",
            required
          ),
          minLength: helpers.withMessage(
            "O motivo deve ter pelo menos 10 caracteres.",
            minLength(10)
          ),
        },
      },
      { motivo: trimmedReason }
    );

    const reasonState = computed(() =>
      v$.value.motivo.$dirty ? !v$.value.motivo.$invalid : null
    );

    const reasonError = computed(
      () => v$.value.motivo.$errors?.[0]?.$message || ""
    );

    const isCanceled = computed(() =>
      isStatus(pedido.value.status, enumMap.CANCELADO)
    );

    const cancellationAuthor = computed(() => {
      if (pedido.value.cancelador?.name) return pedido.value.cancelador.name;

      if (
        Object.prototype.hasOwnProperty.call(
          CANCELLATION_ORIGINS,
          pedido.value.origem
        )
      ) {
        return CANCELLATION_ORIGINS[pedido.value.origem];
      }

      return "Não informado";
    });
    const pedidoId = computed(() => `#${route.params.id || "-"}`);
    const allowedCancelOrder = computed(() => {
      if (!pedido.value.status) return false;

      return !isStatus(pedido.value?.status, [enumMap.PAGO, enumMap.CANCELADO]);
    });

    function onModalOrderDisplay() {
      motivo.value = "";
      v$.value.$reset();
      isModalOrderDisplay.value = true;
    }

    async function cancelOrder(event) {
      event?.preventDefault();

      if (isCanceling.value || !allowedCancelOrder.value) return;
      if (!(await v$.value.$validate())) return;

      if (isCanceling.value) return;
      const id = route.params?.id;
      if (!id) return;

      isCanceling.value = true;

      try {
        const result = await ActionCancelOrder({
          id,
          motivo: trimmedReason.value,
        });
        isModalOrderDisplay.value = false;
        const message = result.message || "Pedido cancelado!";

        notifications.addNotification({
          message,
          type: "success",
        });

        try {
          await Promise.all([ActionShow(id), ActionShowPaymentsHistory(id)]);
        } catch (error) {
          notifications.addNotification({
            message:
              "Pedido cancelado, mas não foi possível atualizar os detalhes. Recarregue a página.",
            type: "warning",
          });
        }
      } catch (error) {
        const message = extractErrorMessage(
          error,
          "Erro ao tentar cancelar pedido!"
        );

        notifications.addNotification({
          message,
          type: "danger",
        });
      } finally {
        isCanceling.value = false;
      }
    }

    function formatCurrency(value) {
      if (value === null || value === undefined || value === "") return "-";

      return `R$ ${numberFormat(Number(value))}`;
    }

    function formatDateTime(value) {
      if (!value) return "-";

      return formatDate(value, "DD/MM/YYYY HH:mm");
    }

    return {
      pedido,
      pagamento,
      pagamentosHistorico,
      pedidoId,
      allowedCancelOrder,
      isCanceled,
      cancellationAuthor,
      motivo,
      v$,
      reasonState,
      reasonError,
      isCanceling,
      isModalOrderDisplay,
      onModalOrderDisplay,
      cancelOrder,
      formatCurrency,
      formatDateTime,
    };
  },
});
</script>

<style scoped>
.cancellation-reason {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
